<script setup>
import { useMessageStore } from '~/stores/messages'

const props = defineProps({
    error: {
        type: Object,
        default: () => ({}),
        validator: (rawValue) => {
            const validations = {
                message: value => value === undefined || typeof value === 'string',
                statusCode: value => value === undefined || typeof value === 'number'
            }

            return Object.entries(validations).every(([key, validation]) => validation(rawValue[key]))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { updateMessages } = messagesStore

// Fallback if message api is not available
const errorHeadline = ref('')
const errorButtonLabel = ref('')

try {
    // Try to fetch message api
    const { data: staticKeys, suspense: messageSuspense } = useFetchMessages()

    onServerPrefetch(async () => {
        await messageSuspense()
        updateMessages(staticKeys.value)
    })

    errorHeadline.value = staticKeys.value?.general_error_headline
    errorButtonLabel.value = staticKeys.value?.general_error_button
} catch (error) {
    console.warn(error)

    // If the message api is not available, set static text keys
    errorHeadline.value = 'Fehler'
    errorButtonLabel.value = 'Alles klar'
}

errorHeadline.value = 'Fehler'
errorButtonLabel.value = 'Alles klar'

// Get headline
const getErrorHeadline = computed(() => `${errorHeadline.value} _${props.error?.statusCode}_`)
const getErrorButtonLabel = computed(() => errorButtonLabel.value)

// Clears the error and redirects to the homepage
const handleClearError = () => {
    clearError({ redirect: '/' })
}

const errorButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'div',
        active: true,
        fn: () => handleClearError()
    }
})

// Get Webview global state
const isWebview = useState('state_isWebview')
</script>

<template>
    <div class="error-page-wrapper">
        <PageHeader v-if="! isWebview" />
        <SideBar v-if="! isWebview" />

        <div class="error-page">
            <div class="error-page-content">
                <ContentMainHeadline
                    :content-main-headline-config="{
                        title: getErrorHeadline,
                        align: 'center',
                        twoLines: true
                    }"
                />

                <p v-if="error.message">
                    {{ error.message }}
                </p>
                <BaseButton :base-button-config="errorButtonConfig">
                    {{ getErrorButtonLabel }}
                </BaseButton>
            </div>
        </div>

        <PageFooter v-if="! isWebview" />
    </div>
</template>

<style lang="scss">
.error-page-wrapper {
    grid-template-rows: auto 1fr auto;
    min-height: 100dvh;
    overflow: hidden;

    @include page-grid;
}

.error-page {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    grid-column: 2/-2;
    grid-row: 2;
    margin-block-start: px2rem(-40);

    @include bp(large1) {
        margin-block-start: px2rem(-60);
    }
}

.error-page-content {
    max-width: 600px;
    margin: 40px auto;
    text-align: center;

    p {
        margin: 0 0 30px;

        @include body1-medium;
    }
}
</style>
